import { weightedVolumeKg } from "./setVolume.js";
import type { Exercise } from "./schemas/exercise.js";
import type { Workout } from "./schemas/workout.js";
import { catalogBySlug, toCatalogSlug } from "./volumeByMuscle.js";
import { calendarWeeksAgo } from "./weekWindow.js";

// Progression series consumed by the Progress surfaces on both web and
// mobile. Lives in shared so the two surfaces compute the same numbers.

export type Point = {
  /** The instant this datum happened, epoch ms. Renderers MUST place it on a
   *  real time axis: a series that stops must LOOK stopped. (HM-1 — this said
   *  "renderers draw equal-spaced", and they did: LineChart took x from the
   *  array index and ignored this field entirely, so 25 days of readings plus
   *  a 5-day hole drew identically to 30 full days. That is how the CGM
   *  pipeline's death went unnoticed for five days.) Ascending. */
  x: number;
  y: number;
  label: string;
};

export type TagVolumeOptions = {
  /** Epoch ms of "now": the current calendar week is the last bucket. */
  nowMs: number;
  /** The exercise catalog the tags are resolved from, by slug. */
  catalog?: ReadonlyArray<Pick<Exercise, "slug" | "tags">>;
};

type WorkoutEntry = Workout["entries"][number];

/** A workout as the volume-by-tag bars read it (#238): the start, and per
 *  entry the exercise key, the fallback tags and the volume. Kept instead of
 *  the full Workout, whose sets nothing else on the card reads. */
export type TagVolumeWorkout = {
  startedAt: string;
  entries: ReadonlyArray<
    Pick<WorkoutEntry, "exerciseId" | "tags"> & { volumeKg: number }
  >;
};

// A fail counts like any set (AP-0p model): reps = clean done, fail is a pure
// label, no exclusion. Loaded-carry contributes weight only.
const entryVolumeKg = (sets: WorkoutEntry["sets"]): number => {
  let kg = 0;
  for (const s of sets) {
    if (s.type === "weighted") kg += weightedVolumeKg(s.weight, s.reps, s.loadedSides);
    else if (s.type === "loaded-carry") kg += s.weight;
  }
  return kg;
};

export const toTagVolumeWorkout = (w: Workout): TagVolumeWorkout => ({
  startedAt: w.startedAt,
  entries: w.entries.map((e) => ({
    exerciseId: e.exerciseId,
    ...(e.tags ? { tags: e.tags } : {}),
    volumeKg: entryVolumeKg(e.sets),
  })),
});

/** One tag's volume per calendar week, oldest first, the current week last
 *  (PG-64 fetta 2: the volume-by-tag bars). Weeks start on `weekStartDay`,
 *  like the Rhythm bars. Loaded-carry contributes weight only (distance lives
 *  on the duration field, not volume).
 *
 *  PG-57: the tags are read from the catalog by exercise key, at read time.
 *  The exercise owns its tags; no writer ever copied them into the entry, so
 *  `entry.tags` stays only as the fallback for an exercise the catalog no
 *  longer has.
 *
 *  Tags are matched through `toCatalogSlug`, so a legacy muscle spelling and
 *  the catalog one («forearm», «forearms») are ONE tag, and an entry carrying
 *  both counts once. A tag outside the muscle vocabulary matches only itself.
 *  Sessions after `nowMs`, older than `weeks` and unparseable dates are
 *  ignored. */
export const bucketTagVolumeByWeek = (
  workouts: ReadonlyArray<Workout | TagVolumeWorkout>,
  tag: string,
  weeks: number,
  weekStartDay: number,
  opts: TagVolumeOptions,
): number[] => {
  const out = new Array<number>(weeks).fill(0);
  const bySlug = catalogBySlug(opts.catalog ?? []);
  const canonical = (t: string): string => toCatalogSlug(t) ?? t;
  const want = canonical(tag);
  for (const w of workouts) {
    const ago = calendarWeeksAgo(
      new Date(w.startedAt).getTime(),
      opts.nowMs,
      weekStartDay,
    );
    // Also drops an unparseable date (NaN fails both sides).
    if (!(ago >= 0 && ago < weeks)) continue;
    let weekVolume = 0;
    for (const entry of w.entries) {
      const tags = bySlug.get(entry.exerciseId)?.tags ?? entry.tags ?? [];
      if (!tags.some((t) => canonical(t) === want)) continue;
      weekVolume += "volumeKg" in entry ? entry.volumeKg : entryVolumeKg(entry.sets);
    }
    const idx = weeks - 1 - ago;
    out[idx] = (out[idx] ?? 0) + weekVolume;
  }
  return out;
};
